import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { useSelector } from "react-redux";
import { PAGES } from "../../enums/PagesEnum";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import { Cancel, ExpandMore } from "@mui/icons-material";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import {
  Add as AddIcon,
  FileUpload as FileUploadIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Done as DoneIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import FileUploadModal from "../../components/common/modalPopup/FileUploadModal";
import { useSubmitModal } from "../../components/common/modalPopup/useSubmitModal";
import { EMAIL_REGEX } from "../fdp/FdpConstants";
import {
  addFdpDriverClientEmail,
  deleteDriverClientEmail,
  fetchFdpAssociatedClients,
  updateFdpAssociatedClients,
  updateInvoiceStatusByPayableDate,
} from "../../app/features/fdp/fdpSlice";
import { toast } from "react-toastify";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import getAxiosError from "../../app/api/axiosError";
import { prepareAndDownloadFile } from "../helper/helper";
import { DriverClientEmailRequest } from "../../app/models/requestModels/fdpRequest";
import { updateStoreManifestList } from "../../app/features/dedicatedReceiving/storeManifestSlice";

function DriverEmailRequestModel(
  fdpId: number,
  clientId: number | undefined,
  emails: string[]
): DriverClientEmailRequest {
  return {
    fdpId: fdpId,
    clientId: clientId,
    emails: emails,
  };
}

const FdpAdminDetails = () => {
  const dispatch = useAppDispatch();
  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.USER
    )
  );
  const isFdp = useSelector(
    (state: RootState) => state.login?.authResponse?.isFdp
  );
  const fdp = useSelector(
    (state: RootState) => state.login?.authResponse?.fdpDetails
  );

  const fdpId = useSelector(
    (state: RootState) => state.login?.authResponse?.fdpDetails?.id 
  );

  const userEmailId = useSelector((state: RootState) => state.login?.authResponse?.userSettings?.userName)

  const { isFdpLoading, fdpAssociatedClients, isDriverEmailLoading } =
    useSelector((state: RootState) => state.fdp);

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        ) ||
        state.login?.authResponse?.userSettings?.roles.includes("FDP_ADMIN")
      : false
  );

  // ***** States *****
  const [fdpDetailsOpen, setFDPDetailsOpen] = React.useState(true);
  const [openAssociatedDetail, setOpenAssociatedDetail] = useState(true);
  const [addNewDriverEmail, setAddNewDriverEmail] = useState(false);
  const [driverEmailText, setDriverEmailText] = useState("");
  const [driverEmailError, setDriverEmailError] = useState(false);
  const [selectedClient, setSelectedClient] = useState<number>();

  // ***** Callbacks *****
  const fetchAssociatedClients = useCallback(async () => {
    try {
      const response = await dispatch(
        fetchFdpAssociatedClients(fdp?.id as number)
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "fdpUsersList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fdpUsersListException",
      });
    }
  }, [dispatch]);

  const handleAddDriverEmail = async (clientId: number) => {
    if (validateEmail(driverEmailText)) {
      try {
        const addDriverEmail = DriverEmailRequestModel(
          fdp?.id as number,
          clientId,
          [driverEmailText.trim()]
        );
        const response = await dispatch(
          addFdpDriverClientEmail(addDriverEmail)
        ).unwrap();
        if (response.isSuccess) {
          reflectChangesPostDriverEmailUpdate(clientId, response.data);
          handleCancelAddingDriverEmail();
          toast.success(response.message!, {
            toastId: "AddDriverEmailSuccess",
          });
        } else {
          handleCancelAddingDriverEmail();
          toast.error(response.message!, {
            toastId: "AddDriverEmailError",
          });
        }
      } catch (error) {
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, {
          toastId: "AddDriverEmailException",
        });
      }
    }
  };

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      fetchAssociatedClients();
    }
    return () => {
      isFunctionRan = false;
    };
  }, [fetchAssociatedClients]);

  useEffect(() => {
    if (driverEmailText.trim()) {
      if (validateEmail(driverEmailText)) {
        setDriverEmailError(false);
      } else {
        setDriverEmailError(true);
      }
    }
  }, [driverEmailText, setDriverEmailError]);

  // Update Invoice Status By PayableDate
  // useEffect(() => {
  //   if(fdpId && userEmailId){
  //     const updateFdpInvoiceRequest = {fdpId, email: userEmailId}
  //     dispatch(updateInvoiceStatusByPayableDate(updateFdpInvoiceRequest))
  //   }
  // }, [dispatch, fdpId, userEmailId])

  const handleBulkAddDriverEmail = async (
    fileMappedData: any[],
    invalidData: any[]
  ) => {
    try {
      const invalidFileData = invalidData.map(({ data, error }) => ({
        Email: data.Email,
        Error: error,
      }));
      if (fileMappedData.length === 0) {
        prepareAndDownloadFile(invalidFileData, "errorDriverEmailCreation_res");
        return false;
      }
      const emails = fileMappedData
        .filter((e) => (Object.keys(e).length ? true : false))
        .map((e) => e.Email.toString().trim());
      const addDriverEmail = DriverEmailRequestModel(
        fdp?.id as number,
        selectedClient,
        emails
      );
      const response = await dispatch(
        addFdpDriverClientEmail(addDriverEmail)
      ).unwrap();
      if (response.isSuccess) {
        if (invalidData.length > 0) {
          prepareAndDownloadFile(
            invalidFileData,
            "errorDriverEmailCreation_res"
          );
          toast.warning("Some emails not saved, please check the file.", {
            toastId: "AddDriverEmailException",
          });
          return true;
        }
        toast.success(response.message!, {
          toastId: "AddDriverEmailSuccess",
        });
        return true;
      } else if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "AddDriverEmailError",
        });
        if (response.data) {
          const mergedData = [
            ...invalidData.map(({ data, error }) => ({
              Email: data.Email,
              Error: error,
            })),
            ...Object.entries(response.data).map(([Email, Error]) => ({
              Email,
              Error,
            })),
          ];
          const transformedData = mergedData.map(({ Email, Error }) => ({
            Email,
            Error,
          }));
          prepareAndDownloadFile(
            transformedData,
            "errorDriverEmailCreation_res"
          );
        }
        return true;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "AddDriverEmailException",
      });
    }
    return false;
  };

  const reflectChangesPostDriverEmailUpdate = (clientId: number, data: []) => {
    const clientToUpdateIndex = fdpAssociatedClients.findIndex(
      (x) => x.id === clientId
    );
    if (clientToUpdateIndex !== -1) {
      // Create a new object with the updated driverEmailIds
      const updatedClient = {
        ...fdpAssociatedClients[clientToUpdateIndex],
        driverEmailIds: data,
      };
      // Create a new array with the updated client at the correct index
      const updatedClients = [
        ...fdpAssociatedClients.slice(0, clientToUpdateIndex),
        updatedClient,
        ...fdpAssociatedClients.slice(clientToUpdateIndex + 1),
      ];
      dispatch(updateFdpAssociatedClients(updatedClients));
    }
  };

  const handleDeleteDriverEmail = async (email: string, clientId: number) => {
    try {
      const deleteDriverEmail = DriverEmailRequestModel(
        fdp?.id as number,
        clientId,
        [email]
      );
      const response = await dispatch(
        deleteDriverClientEmail(deleteDriverEmail)
      ).unwrap();
      if (response.isSuccess) {
        reflectChangesPostDriverEmailUpdate(clientId, response.data);
        toast.success(response.message!, {
          toastId: "DeleteDriverEmailSuccess",
        });
      } else {
        toast.error(response.message!, {
          toastId: "DeleteDriverEmailError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "DeleteDriverEmailException",
      });
    }
  };

  const validateEmail = (email: string) => {
    return EMAIL_REGEX.test(email);
  };

  const handleCancelAddingDriverEmail = () => {
    setAddNewDriverEmail(false);
    setDriverEmailError(false);
    setDriverEmailText("");
  };

  const closeDriverEmailPopup = (popupState: any) => {
    popupState.close();
    handleCancelAddingDriverEmail();
  };

  const handleImportClick = (clientId: number) => {
    setSelectedClient(clientId);
    openFileUploadModal();
  };

  const [fileUploadModal, openFileUploadModal] = useSubmitModal(
    (closeFileUploadModal) => (
      <FileUploadModal
        closeFileUploadModal={closeFileUploadModal}
        expectedColumns={[
          {
            name: "Email",
            mandatory: true,
            type: "string",
            regex: EMAIL_REGEX,
            camelCaseName: "Email",
          },
        ]}
        asyncSubmitOperation={handleBulkAddDriverEmail}
        onCompleted={fetchAssociatedClients}
        sampleFileName={"BulkDriverEmailTemplate.xlsx"}
      />
    )
  );

  return (
    <Box mb={1}>
      <Accordion elevation={5} expanded={fdpDetailsOpen}>
        <AccordionSummary
          onClick={() => setFDPDetailsOpen(!fdpDetailsOpen)}
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              color: "white",
            }}
          >
            <Typography>FDP Details</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
          <Grid
            container
            rowSpacing={1}
            columnSpacing={2}
            sx={{ alignSelf: "center" }}
          >
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <TextField
                label="Comapany Name"
                id="comapanyName"
                defaultValue={fdp?.companyName}
                variant="filled"
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <TextField
                label="GC Account"
                id="gcAccount"
                defaultValue={fdp?.gcAccount}
                variant="filled"
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <TextField
                label="Point Of contact"
                id="pointOfContact"
                defaultValue={fdp?.pointOfContact}
                variant="filled"
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <TextField
                label="Dispatcher Name"
                id="dispatcherName"
                defaultValue={fdp?.dispatcherName}
                variant="filled"
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <TextField
                label="City"
                id="city"
                defaultValue={fdp?.city}
                variant="filled"
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <TextField
                label="State"
                id="state"
                defaultValue={fdp?.state}
                variant="filled"
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion sx={{ mt: 1 }} elevation={5} expanded={openAssociatedDetail}>
        <AccordionSummary
          onClick={() => setOpenAssociatedDetail(!openAssociatedDetail)}
          expandIcon={
            <>
              <IconButton
                onClick={() => setOpenAssociatedDetail(!openAssociatedDetail)}
                sx={{ color: "white" }}
              >
                <ExpandMore sx={{ color: "white" }} />
              </IconButton>
            </>
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              color: "white",
            }}
          >
            <Typography>Associated Clients</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {fdpAssociatedClients.length === 0 && (
              <Typography>No Associated Clients</Typography>
            )}
            {fdpAssociatedClients.map((client, index) => (
              <Grid container rowSpacing={2} columnSpacing={2} key={index}>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <TextField
                    margin="normal"
                    id={"clientName" + client.clientName}
                    label="Client Name"
                    size="small"
                    fullWidth
                    defaultValue={client.clientName}
                    variant="filled"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3} lg={3}>
                  <TextField
                    margin="normal"
                    id={"clientId" + client.id}
                    label="Client ID"
                    size="small"
                    fullWidth
                    defaultValue={client.id}
                    variant="filled"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                {client.storeIds && client.storeIds.length > 0 && (
                  <>
                    <Grid item xs={12} sm={4} md={2} lg={2}>
                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <PopupState
                          variant="popover"
                          popupId="stores-popup-menu"
                        >
                          {(popupState) => (
                            <>
                              <Button
                                sx={{ height: "45px", width: "200px" }}
                                size="large"
                                variant="outlined"
                                {...bindTrigger(popupState)}
                              >
                                Stores
                                <KeyboardArrowDownIcon sx={{ ml: 1 }} />
                              </Button>
                              <Menu {...bindMenu(popupState)}>
                                {client.storeIds.map((store, index) => (
                                  <MenuItem
                                    sx={{ width: "200px" }}
                                    key={index}
                                    onClick={popupState.close}
                                  >
                                    {store}
                                  </MenuItem>
                                ))}
                              </Menu>
                            </>
                          )}
                        </PopupState>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3} lg={3}>
                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <PopupState
                          variant="popover"
                          popupId="driverEmail-popup-menu"
                        >
                          {(popupState) => (
                            <>
                              <Button
                                sx={{ height: "45px", width: "300px" }}
                                size="large"
                                variant="outlined"
                                {...bindTrigger(popupState)}
                              >
                                Driver Emails
                                <KeyboardArrowDownIcon sx={{ ml: 1 }} />
                              </Button>
                              <Menu
                                sx={{ height: "400px" }}
                                {...bindMenu(popupState)}
                                onClose={() =>
                                  closeDriverEmailPopup(popupState)
                                }
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "end",
                                  }}
                                >
                                  {!addNewDriverEmail && (
                                    <CustomTooltip title="Add New">
                                      <IconButton
                                        onClick={() =>
                                          setAddNewDriverEmail(true)
                                        }
                                      >
                                        <AddIcon
                                          sx={{ color: "primary.main" }}
                                        />
                                      </IconButton>
                                    </CustomTooltip>
                                  )}
                                  <CustomTooltip title="Upload">
                                    <IconButton
                                      onClick={() =>
                                        handleImportClick(client.id)
                                      }
                                    >
                                      <FileUploadIcon
                                        sx={{ color: "primary.main" }}
                                      />
                                    </IconButton>
                                  </CustomTooltip>
                                  <CustomTooltip title="Close">
                                    <IconButton
                                      onClick={() =>
                                        closeDriverEmailPopup(popupState)
                                      }
                                    >
                                      <Cancel sx={{ color: "primary.main" }} />
                                    </IconButton>
                                  </CustomTooltip>
                                </Box>
                                {addNewDriverEmail && (
                                  <TextField
                                    onKeyDown={(e) => e.stopPropagation()}
                                    id="driverEmail"
                                    label="Driver Email"
                                    size="small"
                                    type="email"
                                    fullWidth
                                    value={driverEmailText}
                                    onChange={(event) =>
                                      setDriverEmailText(event.target.value)
                                    }
                                    variant="filled"
                                    error={driverEmailError}
                                    helperText={
                                      driverEmailError
                                        ? "Provide valid email"
                                        : ""
                                    }
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <IconButton
                                            disabled={!driverEmailText.trim()}
                                            title="Save"
                                            edge="end"
                                            onClick={() =>
                                              handleAddDriverEmail(client.id)
                                            }
                                          >
                                            <DoneIcon
                                              sx={{
                                                mr: 1,
                                                color: "primary.main",
                                              }}
                                            />
                                          </IconButton>
                                          <IconButton
                                            title="Cancel"
                                            edge="end"
                                            onClick={
                                              handleCancelAddingDriverEmail
                                            }
                                          >
                                            <CancelIcon
                                              sx={{
                                                color: "primary.main",
                                              }}
                                            />
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                                {isDriverEmailLoading && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <CircularProgress color="primary" />
                                  </Box>
                                )}
                                {client.driverEmailIds &&
                                  client.driverEmailIds.length === 0 && (
                                    <MenuItem
                                      sx={{ width: "300px" }}
                                      key={index}
                                      onClick={popupState.close}
                                    >
                                      No Emails Exists
                                    </MenuItem>
                                  )}
                                {client.driverEmailIds.map((email, index) => (
                                  <MenuItem
                                    key={index}
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      height: "35px",
                                      width: "300px",
                                      pl: 1,
                                      pr: 0,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        overflowX: "auto",
                                        "&::-webkit-scrollbar": {
                                          display: "none",
                                        },
                                        scrollbarWidth: "none",
                                      }}
                                    >
                                      {email}
                                    </Typography>
                                    <CustomTooltip title="Delete">
                                      <IconButton
                                        onClick={() =>
                                          handleDeleteDriverEmail(
                                            email,
                                            client.id
                                          )
                                        }
                                      >
                                        <DeleteIcon
                                          sx={{
                                            color: "primary.main",
                                          }}
                                        />
                                      </IconButton>
                                    </CustomTooltip>
                                  </MenuItem>
                                ))}
                              </Menu>
                            </>
                          )}
                        </PopupState>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
      {fileUploadModal}
    </Box>
  );
};

export default FdpAdminDetails;
